import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Plus, Trash2, ChevronLeft, ChevronRight,
  Users, DollarSign, Clock, Calendar as CalendarIcon,
  UserPlus, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAppContext } from "@/lib/onboardingContext";
import { getCurrencySymbol, convertFromUSD } from "@/lib/currency";
import WeeklyStaffForecast from "@/components/staff-forecast/WeeklyStaffForecast";

interface StaffMember {
  id: string;
  name: string;
  role: string;
  hourlyRate: number;
  color: string;
}

interface Shift {
  id: string;
  staffId: string;
  date: string; // YYYY-MM-DD
  startHour: number; // 0-23
  endHour: number; // 0-23
}

const STAFF_COLORS = [
  "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500",
  "bg-violet-500", "bg-cyan-500", "bg-orange-500", "bg-pink-500",
  "bg-teal-500", "bg-indigo-500", "bg-lime-500", "bg-fuchsia-500",
];

// Time grid: 6 AM (6) through 2 AM next day (26). 30-min increments for picker.
const START_HOUR = 6;
const END_HOUR = 26; // 2 AM next day
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => i + START_HOUR); // 6..26
const HALF_HOURS = Array.from({ length: (END_HOUR - START_HOUR) * 2 + 1 }, (_, i) => START_HOUR + i * 0.5); // 6, 6.5, ..., 26
const SLOT_HEIGHT_PX = 28; // height of each 30-min slot
const HOUR_HEIGHT_PX = SLOT_HEIGHT_PX * 2;

function formatHour(h: number): string {
  // Supports fractional and >=24 (next-day) values
  const whole = Math.floor(h);
  const minutes = Math.round((h - whole) * 60);
  const normalized = whole % 24;
  const suffix = whole >= 24 ? " (+1)" : "";
  let display: string;
  if (normalized === 0) display = "12 AM";
  else if (normalized === 12) display = "12 PM";
  else display = normalized < 12 ? `${normalized} AM` : `${normalized - 12} PM`;
  if (minutes > 0) {
    // insert minutes before the AM/PM
    const [num, ampm] = display.split(" ");
    display = `${num}:${String(minutes).padStart(2, "0")} ${ampm}`;
  }
  return display + suffix;
}

function getWeekDates(baseDate: Date): Date[] {
  const start = new Date(baseDate);
  start.setDate(start.getDate() - start.getDay() + 1); // Monday
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

function dateStr(d: Date): string {
  return d.toISOString().split("T")[0];
}

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_NAMES_FULL = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function StaffRota() {
  const navigate = useNavigate();
  const { results: rawResults, onboarding, ensureResults } = useAppContext();
  const results = rawResults ?? ensureResults();
  const city = (onboarding.city || "").toLowerCase();
  const cs = getCurrencySymbol(city);

  // Initialize staff from staffing data
  const initialStaff: StaffMember[] = useMemo(() => {
    if (!results?.staffing) return [];
    const members: StaffMember[] = [];
    results.staffing.forEach((role, ri) => {
      for (let i = 0; i < role.count; i++) {
        members.push({
          id: `staff-${ri}-${i}`,
          name: `${role.role}${role.count > 1 ? ` ${i + 1}` : ""}`,
          role: role.role,
          hourlyRate: role.monthlyEstimate / role.count / 160, // rough hourly from monthly
          color: STAFF_COLORS[(ri + i) % STAFF_COLORS.length],
        });
      }
    });
    return members;
  }, [results?.staffing]);

  const [staff, setStaff] = useState<StaffMember[]>(initialStaff);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  const [addShiftOpen, setAddShiftOpen] = useState(false);
  const [newStaff, setNewStaff] = useState({ name: "", role: "", hourlyRate: 15 });
  const [newShift, setNewShift] = useState({ staffId: "", date: "", startHour: 9, endHour: 17 });
  const [view, setView] = useState<"week" | "day">("week");
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState<"schedule" | "forecast">("schedule");

  const weekDates = useMemo(() => getWeekDates(currentWeek), [currentWeek]);

  const prevWeek = () => {
    const d = new Date(currentWeek);
    d.setDate(d.getDate() - 7);
    setCurrentWeek(d);
  };
  const nextWeek = () => {
    const d = new Date(currentWeek);
    d.setDate(d.getDate() + 7);
    setCurrentWeek(d);
  };

  const addStaffMember = useCallback(() => {
    if (!newStaff.name || !newStaff.role) return;
    setStaff(prev => [...prev, {
      id: `staff-${Date.now()}`,
      name: newStaff.name,
      role: newStaff.role,
      hourlyRate: newStaff.hourlyRate,
      color: STAFF_COLORS[prev.length % STAFF_COLORS.length],
    }]);
    setNewStaff({ name: "", role: "", hourlyRate: 15 });
    setAddStaffOpen(false);
  }, [newStaff]);

  const removeStaff = useCallback((id: string) => {
    setStaff(prev => prev.filter(s => s.id !== id));
    setShifts(prev => prev.filter(s => s.staffId !== id));
  }, []);

  const addShift = useCallback(() => {
    if (!newShift.staffId || !newShift.date) return;
    setShifts(prev => [...prev, {
      id: `shift-${Date.now()}`,
      ...newShift,
    }]);
    setNewShift({ staffId: "", date: "", startHour: 9, endHour: 17 });
    setAddShiftOpen(false);
  }, [newShift]);

  const removeShift = useCallback((id: string) => {
    setShifts(prev => prev.filter(s => s.id !== id));
  }, []);

  // Calculate weekly labor cost
  const weeklyLaborCost = useMemo(() => {
    let total = 0;
    for (const shift of shifts) {
      const weekDateStrs = weekDates.map(d => dateStr(d));
      if (!weekDateStrs.includes(shift.date)) continue;
      const member = staff.find(s => s.id === shift.staffId);
      if (!member) continue;
      const hours = shift.endHour - shift.startHour;
      total += hours * member.hourlyRate;
    }
    return total;
  }, [shifts, weekDates, staff]);

  const totalWeeklyHours = useMemo(() => {
    let total = 0;
    const weekDateStrs = weekDates.map(d => dateStr(d));
    for (const shift of shifts) {
      if (!weekDateStrs.includes(shift.date)) continue;
      total += shift.endHour - shift.startHour;
    }
    return total;
  }, [shifts, weekDates]);

  const shiftsOnDate = (date: string) => shifts.filter(s => s.date === date);

  const getStaffById = (id: string) => staff.find(s => s.id === id);

  // results is guaranteed non-null via ensureResults() above.

  const weekLabel = `${weekDates[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDates[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-medium text-foreground">Staff Rota & Scheduling</h1>
            <p className="text-sm text-muted-foreground">Manage shifts, track labor costs, and control staffing</p>
          </div>
          {activeTab === "schedule" && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setAddStaffOpen(true)}>
                <UserPlus className="w-4 h-4 mr-1" /> Add Staff
              </Button>
              <Button size="sm" onClick={() => setAddShiftOpen(true)}>
                <Plus className="w-4 h-4 mr-1" /> Add Shift
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="container pt-6 max-w-6xl">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "schedule" | "forecast")} className="mb-6">
          <TabsList>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
            <TabsTrigger value="forecast">Weekly Staff Forecast</TabsTrigger>
          </TabsList>
        </Tabs>
        {activeTab === "forecast" ? (
          <WeeklyStaffForecast />
        ) : (
        <>
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Users className="w-3.5 h-3.5" /> Staff Members
            </div>
            <p className="text-2xl font-medium text-foreground">{staff.length}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Clock className="w-3.5 h-3.5" /> Weekly Hours
            </div>
            <p className="text-2xl font-medium text-foreground">{totalWeeklyHours}h</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <DollarSign className="w-3.5 h-3.5" /> Weekly Labor Cost
            </div>
            <p className="text-2xl font-medium text-foreground">
              {cs}{Math.round(convertFromUSD(weeklyLaborCost, city)).toLocaleString()}
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <DollarSign className="w-3.5 h-3.5" /> Monthly Estimate
            </div>
            <p className="text-2xl font-medium text-foreground">
              {cs}{Math.round(convertFromUSD(weeklyLaborCost * 4.33, city)).toLocaleString()}
            </p>
          </motion.div>
        </div>

        {/* Staff List */}
        <div className="mb-6">
          <h2 className="font-heading font-medium text-foreground text-sm mb-3">Team</h2>
          <div className="flex flex-wrap gap-2">
            {staff.map(member => (
              <div key={member.id} className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
                <span className="text-sm font-medium text-foreground">{member.name}</span>
                <Badge variant="secondary" className="text-xs">{member.role}</Badge>
                <span className="text-xs text-muted-foreground">
                  {cs}{convertFromUSD(member.hourlyRate, city).toFixed(0)}/hr
                </span>
                <button onClick={() => removeStaff(member.id)} className="text-muted-foreground hover:text-destructive ml-1">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {staff.length === 0 && (
              <p className="text-sm text-muted-foreground">No staff members yet. Add your team to start scheduling.</p>
            )}
          </div>
        </div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={prevWeek}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-muted-foreground" />
              <span className="font-heading font-medium text-foreground text-sm">{weekLabel}</span>
            </div>
            <Button variant="outline" size="icon" onClick={nextWeek}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setCurrentWeek(new Date())}>Today</Button>
        </div>

        {/* Calendar Time Grid */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              {/* Day headers row — sticky time column on left */}
              <div className="grid border-b border-border" style={{ gridTemplateColumns: "64px repeat(7, minmax(0, 1fr))" }}>
                <div className="border-r border-border bg-muted/30" />
                {weekDates.map((date, i) => {
                  const isToday = dateStr(date) === dateStr(new Date());
                  const dayShifts = shiftsOnDate(dateStr(date));
                  const dayHours = dayShifts.reduce((s, sh) => s + (sh.endHour - sh.startHour), 0);
                  return (
                    <div
                      key={i}
                      className={`p-2 text-center border-r last:border-r-0 border-border cursor-pointer hover:bg-muted/30 transition-colors ${isToday ? "bg-primary/5" : ""}`}
                      onClick={() => { setSelectedDay(date); setView("day"); }}
                    >
                      <p className="text-xs text-muted-foreground">{DAY_NAMES[i]}</p>
                      <p className={`text-base font-medium ${isToday ? "text-primary" : "text-foreground"}`}>
                        {date.getDate()}
                      </p>
                      {dayHours > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">{dayHours}h</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Time grid body */}
              <div
                className="relative grid"
                style={{
                  gridTemplateColumns: "64px repeat(7, minmax(0, 1fr))",
                  height: `${(END_HOUR - START_HOUR) * HOUR_HEIGHT_PX}px`,
                }}
              >
                {/* Time labels column */}
                <div className="border-r border-border bg-muted/20 relative">
                  {HOURS.slice(0, -1).map((h) => (
                    <div
                      key={h}
                      className="absolute left-0 right-0 px-1 text-[10px] text-muted-foreground text-right pr-2"
                      style={{ top: `${(h - START_HOUR) * HOUR_HEIGHT_PX}px`, height: `${HOUR_HEIGHT_PX}px` }}
                    >
                      <span className="-translate-y-1/2 inline-block">{formatHour(h)}</span>
                    </div>
                  ))}
                </div>

                {/* Day columns */}
                {weekDates.map((date, di) => {
                  const ds = dateStr(date);
                  const dayShifts = shiftsOnDate(ds);
                  const isToday = ds === dateStr(new Date());
                  return (
                    <div
                      key={di}
                      className={`relative border-r last:border-r-0 border-border ${isToday ? "bg-primary/[0.03]" : ""}`}
                    >
                      {/* Hour gridlines */}
                      {HOURS.map((h) => (
                        <div
                          key={h}
                          className="absolute left-0 right-0 border-t border-border/60"
                          style={{ top: `${(h - START_HOUR) * HOUR_HEIGHT_PX}px` }}
                        />
                      ))}
                      {/* Half-hour gridlines (lighter) */}
                      {HOURS.slice(0, -1).map((h) => (
                        <div
                          key={`half-${h}`}
                          className="absolute left-0 right-0 border-t border-dashed border-border/30"
                          style={{ top: `${(h - START_HOUR) * HOUR_HEIGHT_PX + SLOT_HEIGHT_PX}px` }}
                        />
                      ))}

                      {/* Empty-state click target */}
                      {dayShifts.length === 0 && staff.length > 0 && (
                        <button
                          onClick={() => {
                            setNewShift({ staffId: staff[0].id, date: ds, startHour: 9, endHour: 17 });
                            setAddShiftOpen(true);
                          }}
                          className="absolute inset-0 opacity-0 hover:opacity-100 flex items-center justify-center bg-muted/20 transition-opacity z-0"
                        >
                          <Plus className="w-4 h-4 text-muted-foreground" />
                        </button>
                      )}

                      {/* Shift blocks */}
                      {dayShifts.map((shift) => {
                        const member = getStaffById(shift.staffId);
                        if (!member) return null;
                        const top = (shift.startHour - START_HOUR) * HOUR_HEIGHT_PX;
                        const height = Math.max(
                          (shift.endHour - shift.startHour) * HOUR_HEIGHT_PX - 2,
                          18
                        );
                        return (
                          <div
                            key={shift.id}
                            className={`${member.color} text-white rounded-md px-1.5 py-1 text-[10px] group absolute left-1 right-1 overflow-hidden shadow-sm z-10`}
                            style={{ top: `${top}px`, height: `${height}px` }}
                          >
                            <div className="font-medium truncate leading-tight">{member.name.split(" ")[0]}</div>
                            <div className="opacity-80 truncate leading-tight">
                              {formatHour(shift.startHour)}–{formatHour(shift.endHour)}
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); removeShift(shift.id); }}
                              className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Remove shift"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {staff.length === 0 && (
            <div className="p-12 text-center text-muted-foreground border-t border-border">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Add staff members and shifts to see your schedule</p>
            </div>
          )}
        </div>

        {/* Daily labor cost breakdown */}
        <div className="mt-6 bg-card border border-border rounded-xl p-4">
          <h3 className="font-heading font-medium text-foreground text-sm mb-3">Daily Cost Breakdown</h3>
          <div className="grid grid-cols-7 gap-2">
            {weekDates.map((date, i) => {
              const dayShifts = shiftsOnDate(dateStr(date));
              let dayCost = 0;
              let dayHrs = 0;
              for (const shift of dayShifts) {
                const member = getStaffById(shift.staffId);
                if (!member) continue;
                const hrs = shift.endHour - shift.startHour;
                dayHrs += hrs;
                dayCost += hrs * member.hourlyRate;
              }
              return (
                <div key={i} className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">{DAY_NAMES[i]}</p>
                  <p className="text-sm font-medium text-foreground">
                    {cs}{Math.round(convertFromUSD(dayCost, city)).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">{dayHrs}h</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Monthly projection from results */}
        {results.totalMonthlyLabor > 0 && (
          <div className="mt-4 p-4 rounded-xl bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground">
              💡 Your business plan estimates <strong>{cs}{Math.round(convertFromUSD(results.totalMonthlyLabor, city)).toLocaleString()}/mo</strong> in labor costs.
              {weeklyLaborCost > 0 && (
                <> Your current schedule projects <strong>{cs}{Math.round(convertFromUSD(weeklyLaborCost * 4.33, city)).toLocaleString()}/mo</strong>. {
                  weeklyLaborCost * 4.33 > results.totalMonthlyLabor * 1.1
                    ? "⚠️ You're running over budget — consider adjusting shifts."
                    : weeklyLaborCost * 4.33 < results.totalMonthlyLabor * 0.8
                    ? "✅ You're under budget — room to add coverage."
                    : "✅ On track with your labor budget."
                }</>
              )}
            </p>
          </div>
        )}
        </>
        )}
      </div>

      {/* Add Staff Dialog */}
      <Dialog open={addStaffOpen} onOpenChange={setAddStaffOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Staff Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground">Name</label>
              <Input
                value={newStaff.name}
                onChange={e => setNewStaff(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Maria Garcia"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Role</label>
              <Input
                value={newStaff.role}
                onChange={e => setNewStaff(p => ({ ...p, role: e.target.value }))}
                placeholder="e.g. Server, Line Cook, Barista"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Hourly Rate (USD)</label>
              <Input
                type="number"
                value={newStaff.hourlyRate}
                onChange={e => setNewStaff(p => ({ ...p, hourlyRate: Number(e.target.value) }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddStaffOpen(false)}>Cancel</Button>
            <Button onClick={addStaffMember}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Shift Dialog */}
      <Dialog open={addShiftOpen} onOpenChange={setAddShiftOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground">Staff Member</label>
              <Select value={newShift.staffId} onValueChange={v => setNewShift(p => ({ ...p, staffId: v }))}>
                <SelectTrigger><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  {staff.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${s.color}`} />
                        {s.name} ({s.role})
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Date</label>
              <Input
                type="date"
                value={newShift.date}
                onChange={e => setNewShift(p => ({ ...p, date: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-foreground">Start Time</label>
                <Select value={String(newShift.startHour)} onValueChange={v => setNewShift(p => ({ ...p, startHour: Number(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {HALF_HOURS.slice(0, -1).map(h => (
                      <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground">End Time</label>
                <Select value={String(newShift.endHour)} onValueChange={v => setNewShift(p => ({ ...p, endHour: Number(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {HALF_HOURS.filter(h => h > newShift.startHour).map(h => (
                      <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {newShift.staffId && newShift.startHour < newShift.endHour && (
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">
                  Shift cost: <strong>{cs}{Math.round(convertFromUSD(
                    (newShift.endHour - newShift.startHour) * (getStaffById(newShift.staffId)?.hourlyRate || 0), city
                  )).toLocaleString()}</strong> ({newShift.endHour - newShift.startHour} hours)
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddShiftOpen(false)}>Cancel</Button>
            <Button onClick={addShift}>Add Shift</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
